import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Customer,
    ProstayGuestDto,
    Reservation,
    ReservationAvailabilitySlot,
    ReservationClash,
    ReservationFieldDef,
    ReservationSettings,
    ReservationStatus,
    Table,
} from '../../interfaces';
import { RESERVATION_STATUS_META, minutesOfTime, serviceWindowsOn } from '../../hooks/useReservations';
import ReservationService from '../../services/ReservationService';
import ReservationFieldInput from './ReservationFieldInput';

type GuestMode = 'walkIn' | 'hotel';

interface Props {
    /** Existing booking to edit, or null when creating. */
    reservation: Reservation | null;
    dateKey: string;
    fields: ReservationFieldDef[];
    settings: ReservationSettings | null;
    tables: Table[];
    /**
     * Where a new booking should start from when the host began somewhere more
     * specific than "+ New" — tapping a gap on the timeline already says which
     * table and roughly what time, and re-asking for both would be rude.
     */
    prefill?: { time?: string; tableIds?: string[] } | null;
    floorId?: string | null;
    /** Prostay is only offered when the property is linked to a PMS. */
    prostayEnabled: boolean;
    onSaved: (r: Reservation) => void;
    onClose: () => void;
    onPickTables: (r: Reservation) => void;
    onSeat?: (r: Reservation) => void;
}

const PARTY_MIN = 1;
const PARTY_MAX = 30;

const icons = {
    close: <svg viewBox='0 0 14 14' width='12' height='12' fill='none'><path d='M13 1L1 13M1 1l12 12' stroke='currentColor' strokeWidth='2' strokeLinecap='round' /></svg>,
    minus: <svg viewBox='0 0 24 24' fill='none'><path d='M5 12h14' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' /></svg>,
    plus: <svg viewBox='0 0 24 24' fill='none'><path d='M12 5v14M5 12h14' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' /></svg>,
    search: <svg viewBox='0 0 24 24' width='14' height='14' fill='none'><circle cx='11' cy='11' r='7' stroke='currentColor' strokeWidth='1.9' /><path d='m20 20-3.6-3.6' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' /></svg>,
    warn: <svg viewBox='0 0 24 24' width='14' height='14' fill='none'><path d='M12 9v4m0 3h.01M10.3 3.9 2.4 17.5A1.9 1.9 0 0 0 4 20.4h16a1.9 1.9 0 0 0 1.6-2.9L13.7 3.9a1.9 1.9 0 0 0-3.4 0Z' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>,
};

const guestFullName = (g: { firstName?: string; lastName?: string }) =>
    `${g.firstName || ''} ${g.lastName || ''}`.trim();

const splitName = (full: string): { firstName: string; lastName?: string } => {
    const parts = full.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: '' };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') || undefined };
};

const ReservationSheet: React.FC<Props> = ({
    reservation,
    dateKey,
    fields,
    settings,
    tables,
    prefill,
    floorId,
    prostayEnabled,
    onSaved,
    onClose,
    onPickTables,
    onSeat,
}) => {
    const isEdit = !!reservation;

    const [date, setDate] = useState(reservation?.date || dateKey);
    const [time, setTime] = useState(reservation?.time || prefill?.time || '19:00');
    const [partySize, setPartySize] = useState(reservation?.partySize ?? 2);
    const [guestName, setGuestName] = useState(reservation ? guestFullName(reservation.guest) : '');
    const [phone, setPhone] = useState(reservation?.guest?.phone || '');
    const [email, setEmail] = useState(reservation?.guest?.email || '');
    const [customerId, setCustomerId] = useState<string | undefined>(reservation?.guest.customerId);
    const [prostay, setProstay] = useState<Reservation['prostay']>(reservation?.prostay);
    const [guestMode, setGuestMode] = useState<GuestMode>(reservation?.prostay ? 'hotel' : 'walkIn');
    const [guestNotes, setGuestNotes] = useState(reservation?.guestNotes || '');
    const [internalNotes, setInternalNotes] = useState(reservation?.internalNotes || '');
    const [customFields, setCustomFields] = useState<Record<string, unknown>>(reservation?.customFields || {});
    const [tableIds, setTableIds] = useState<string[]>(reservation?.tableIds || prefill?.tableIds || []);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [slots, setSlots] = useState<ReservationAvailabilitySlot[]>([]);
    /** Set when the save came back refused because the table is already taken. */
    const [clashes, setClashes] = useState<ReservationClash[]>([]);

    // Guest lookup (Prostay in-house guests, or the TableView customer list)
    const [term, setTerm] = useState('');
    const [searching, setSearching] = useState(false);
    const [hotelResults, setHotelResults] = useState<ProstayGuestDto[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [resultsOpen, setResultsOpen] = useState(false);
    const searchDebounce = useRef<number | undefined>(undefined);

    const formFields = useMemo(
        () => [...fields].sort((a, b) => a.sortOrder - b.sortOrder),
        [fields],
    );

    // Keep the assigned tables in sync when the floor-plan picker writes back.
    useEffect(() => {
        if (reservation) setTableIds(reservation.tableIds);
    }, [reservation]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Walk-in mode searches the CRM; hotel mode hits the PMS guest search that
    // the table-setup dialog already uses.
    const runSearch = useCallback(async (q: string, mode: GuestMode) => {
        if (!q.trim()) { setHotelResults([]); setCustomers([]); return; }
        setSearching(true);
        try {
            if (mode === 'hotel') {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/Prostay/search-prostay-guest`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ currentPage: 1, pageSize: 25, searchTerm: q, filterDuplicatesByEmail: false }),
                    credentials: 'include',
                });
                const data = await res.json();
                setHotelResults(data?.data || []);
            } else {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/Customer/filter`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ currentPage: 1, pageSize: 25, searchTerm: q }),
                    credentials: 'include',
                });
                const data = await res.json();
                const list: Customer[] = Array.isArray(data) ? data : (data?.data || []);
                const lower = q.trim().toLowerCase();
                setCustomers(list.filter(c =>
                    `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().includes(lower) ||
                    (c.email || '').toLowerCase().includes(lower) ||
                    (c.phone || '').toLowerCase().includes(lower),
                ));
            }
            setResultsOpen(true);
        } catch (e) {
            console.warn('[ReservationSheet] guest search failed', e);
        } finally {
            setSearching(false);
        }
    }, []);

    useEffect(() => {
        window.clearTimeout(searchDebounce.current);
        searchDebounce.current = window.setTimeout(() => runSearch(term, guestMode), 300);
        return () => window.clearTimeout(searchDebounce.current);
    }, [term, guestMode, runSearch]);

    const pickHotelGuest = (g: ProstayGuestDto) => {
        setGuestName(guestFullName(g));
        setEmail(g.email || '');
        setPhone(g.mobileNumber || g.fixNumber || '');
        setProstay({
            reservationId: g.reservationId,
            reservationNumber: g.reservationNumber,
            guestId: g.id,
            roomNumber: g.roomNumber,
        });
        setCustomerId(undefined);
        setTerm('');
        setResultsOpen(false);
    };

    const pickCustomer = (c: Customer) => {
        setGuestName(`${c.firstName || ''} ${c.lastName || ''}`.trim());
        setEmail(c.email || '');
        setPhone(c.phone || '');
        setCustomerId(c.id);
        setProstay(undefined);
        setTerm('');
        setResultsOpen(false);

        // What we already know about this guest — allergies and the like — without
        // overwriting anything the person taking the booking has typed.
        const known = c.reservationFields;
        if (known) {
            setCustomFields(prev => {
                const next = { ...prev };
                for (const f of fields) {
                    if (!f.onCustomerProfile) continue;
                    const value = known[f.key];
                    if (value === undefined || value === null || value === '') continue;
                    const current = next[f.key];
                    if (current === undefined || current === null || current === '') next[f.key] = value;
                }
                return next;
            });
        }
    };

    const clearGuestLink = () => {
        setProstay(undefined);
        setCustomerId(undefined);
    };

    const switchMode = (mode: GuestMode) => {
        setGuestMode(mode);
        setTerm('');
        setResultsOpen(false);
        setHotelResults([]);
        setCustomers([]);
        clearGuestLink();
    };

    const turnTime = useMemo(() => {
        if (reservation?.durationMinutes) return reservation.durationMinutes;
        // A rule is a floor ("N or more guests"); the biggest threshold the party meets wins.
        const rule = (settings?.durationByPartySize || [])
            .filter(r => partySize >= r.fromPartySize)
            .sort((a, b) => b.fromPartySize - a.fromPartySize)[0];
        return rule ? rule.minutes : (settings?.defaultDurationMinutes || 90);
    }, [reservation, settings, partySize]);

    /**
     * Times inside a configured service window, at the settings' granularity.
     * Empty when the venue doesn't use windows, which drops the picker back to a
     * plain time input — any time of day can be booked.
     */
    const timeOptions = useMemo(() => {
        if (!settings || !serviceWindowsOn(settings)) return [];
        const dow = new Date(`${date}T12:00:00`).getDay();
        const out: string[] = [];
        settings.services
            .filter(s => s.isActive && s.daysOfWeek.includes(dow))
            .forEach(s => {
                for (let m = minutesOfTime(s.startTime); m <= minutesOfTime(s.endTime) - 30; m += settings.slotIntervalMinutes) {
                    out.push(`${`${Math.floor(m / 60)}`.padStart(2, '0')}:${`${m % 60}`.padStart(2, '0')}`);
                }
            });
        return out;
    }, [settings, date]);

    /**
     * What the floor can take on this date for this party. Asked of the server
     * rather than worked out here: it can see every floor and the whole day, and
     * another device may have booked the last table a moment ago.
     */
    useEffect(() => {
        let live = true;
        const t = window.setTimeout(() => {
            ReservationService
                .availability(date, partySize, floorId || undefined, reservation?.id)
                .then(found => { if (live) setSlots(found); });
        }, 200);
        return () => { live = false; window.clearTimeout(t); };
    }, [date, partySize, floorId, reservation?.id]);

    // A warning about 19:00 on T7 says nothing once either of them has changed.
    useEffect(() => { setClashes([]); }, [date, time, partySize, tableIds]);

    /** Free tables at a given time, or undefined when that time has no slot. */
    const freeAt = useCallback((at: string): number | undefined => {
        const slot = slots.find(s => s.time === at);
        return slot ? slot.tableIds.length : undefined;
    }, [slots]);

    const freeNow = freeAt(time);

    /** The nearest time on either side of a full slot that could seat them. */
    const nearestFree = useMemo(() => {
        if (freeNow === undefined || freeNow > 0) return null;
        const wanted = minutesOfTime(time);
        return slots
            .filter(s => s.available)
            .map(s => ({ time: s.time, gap: Math.abs(minutesOfTime(s.time) - wanted) }))
            .sort((a, b) => a.gap - b.gap)[0] || null;
    }, [slots, time, freeNow]);

    const tableLabel = tableIds
        .map(id => tables.find(t => t.id === id)?.tableNo)
        .filter(Boolean)
        .join(' + ');

    const missingRequired = formFields
        .filter(f => f.required)
        .filter(f => {
            const v = customFields[f.key];
            return v == null || v === '' || v === false || (Array.isArray(v) && v.length === 0);
        });

    const handleSave = async (allowOverlap = false) => {
        if (!guestName.trim()) {
            setError('Add a guest name.');
            return;
        }
        if (missingRequired.length > 0) {
            setError(`${missingRequired[0].label} is required.`);
            return;
        }
        setError('');
        setSaving(true);
        try {
            const { firstName, lastName } = splitName(guestName);
            const payload: Partial<Reservation> = {
                id: reservation?.id,
                date,
                time,
                partySize,
                durationMinutes: turnTime,
                floorId: floorId || reservation?.floorId,
                tableIds,
                guest: {
                    customerId,
                    firstName,
                    lastName,
                    email: email || undefined,
                    phone: phone || undefined,
                    isRepeat: reservation?.guest.isRepeat,
                    isVip: reservation?.guest.isVip,
                },
                prostay,
                occasion: (customFields.occasion as string) || undefined,
                guestNotes: guestNotes || undefined,
                internalNotes: internalNotes || undefined,
                customFields,
                tags: reservation?.tags || [],
                source: reservation?.source || (prostay ? 'prostay' : 'phone'),
            };
            const result = await ReservationService.save(payload, allowOverlap);
            if (result.ok) {
                setClashes([]);
                onSaved(result.reservation);
                return;
            }
            if (result.clashes.length > 0) {
                setClashes(result.clashes);
                return;
            }
            setError('Could not save the reservation. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleStatus = async (status: ReservationStatus) => {
        if (!reservation) return;
        setSaving(true);
        try {
            const saved = status === 'cancelled'
                ? await ReservationService.cancel(reservation.id)
                : status === 'noShow'
                    ? await ReservationService.markNoShow(reservation.id)
                    : status === 'arrived'
                        ? await ReservationService.markArrived(reservation.id)
                        : await ReservationService.confirm(reservation.id);
            if (saved) onSaved(saved);
        } finally {
            setSaving(false);
        }
    };

    const statusMeta = reservation ? RESERVATION_STATUS_META[reservation.status] : null;
    const isClosed = reservation
        ? ['completed', 'cancelled', 'noShow'].includes(reservation.status)
        : false;

    return (
        <div className='resv-scrim' onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className='resv-sheet' onMouseDown={(e) => e.stopPropagation()} role='dialog' aria-label='Reservation'>
                <div className='resv-sheet__head'>
                    <div className='resv-sheet__headtext'>
                        {reservation && (
                            <div className='resv-sheet__eyebrow'>
                                {reservation.code}
                                {statusMeta && (
                                    <span className='resv-sheet__status' style={{ ['--accent' as any]: statusMeta.accent }}>
                                        {statusMeta.label}
                                    </span>
                                )}
                            </div>
                        )}
                        <div className='resv-sheet__title'>{isEdit ? 'Reservation details' : 'New reservation'}</div>
                        <div className='resv-sheet__sub'>
                            {isEdit ? 'Update the booking or move it along the service.' : 'Take a booking for the floor.'}
                        </div>
                    </div>
                    <button className='resv-x' onClick={onClose} aria-label='Close'>{icons.close}</button>
                </div>

                <div className='resv-sheet__body'>
                    {/* ── When ─────────────────────────────────────────────── */}
                    <section className='resv-sec'>
                        <div className='resv-sec__title'>When</div>
                        <div className='resv-grid2'>
                            <div className='resv-field'>
                                <label className='resv-label' htmlFor='resv-date'>Date</label>
                                <input
                                    id='resv-date'
                                    type='date'
                                    className='resv-input'
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </div>
                            <div className='resv-field'>
                                <label className='resv-label' htmlFor='resv-time'>Time</label>
                                {timeOptions.length > 0 ? (
                                    <select
                                        id='resv-time'
                                        className='resv-input resv-select'
                                        value={time}
                                        onChange={(e) => setTime(e.target.value)}
                                    >
                                        {!timeOptions.includes(time) && <option value={time}>{time}</option>}
                                        {timeOptions.map(t => {
                                            const free = freeAt(t);
                                            return (
                                                <option key={t} value={t}>
                                                    {t}{free === 0 ? ' — full' : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                ) : (
                                    <input
                                        id='resv-time'
                                        type='time'
                                        className='resv-input'
                                        value={time}
                                        onChange={(e) => setTime(e.target.value)}
                                    />
                                )}
                            </div>
                        </div>

                        {/* What the floor can actually take, so nobody has to hold the
                          * whole service in their head while a guest waits on the phone. */}
                        {freeNow !== undefined && (
                            freeNow > 0 ? (
                                <div className='resv-avail'>
                                    {freeNow === 1
                                        ? `One table free at ${time} for ${partySize}.`
                                        : `${freeNow} tables free at ${time} for ${partySize}.`}
                                </div>
                            ) : (
                                <div className='resv-avail is-full'>
                                    {icons.warn}
                                    <span>
                                        Nothing free at {time} for {partySize}.
                                        {nearestFree ? ' Nearest is ' : ' Nothing else fits today either.'}
                                    </span>
                                    {nearestFree && (
                                        <button
                                            type='button'
                                            className='resv-avail__jump'
                                            onClick={() => setTime(nearestFree.time)}
                                        >{nearestFree.time}</button>
                                    )}
                                </div>
                            )
                        )}

                        <div className='resv-field'>
                            <label className='resv-label'>Party size</label>
                            <div className='resv-stepper'>
                                <button
                                    type='button'
                                    className='resv-stepper__btn'
                                    onClick={() => setPartySize(p => Math.max(PARTY_MIN, p - 1))}
                                    disabled={partySize <= PARTY_MIN}
                                    aria-label='Fewer guests'
                                >{icons.minus}</button>
                                <div className='resv-stepper__val'>
                                    {partySize}
                                    <span>{partySize === 1 ? 'guest' : 'guests'}</span>
                                </div>
                                <button
                                    type='button'
                                    className='resv-stepper__btn'
                                    onClick={() => setPartySize(p => Math.min(PARTY_MAX, p + 1))}
                                    disabled={partySize >= PARTY_MAX}
                                    aria-label='More guests'
                                >{icons.plus}</button>
                                <span className='resv-stepper__hint'>{turnTime} min turn</span>
                            </div>
                        </div>
                    </section>

                    {/* ── Who ──────────────────────────────────────────────── */}
                    <section className='resv-sec'>
                        <div className='resv-sec__title'>Who</div>

                        {prostayEnabled && (
                            <div className='resv-seg resv-seg--full'>
                                <button
                                    type='button'
                                    className={`resv-seg__btn${guestMode === 'walkIn' ? ' is-active' : ''}`}
                                    onClick={() => switchMode('walkIn')}
                                >Guest / walk-in</button>
                                <button
                                    type='button'
                                    className={`resv-seg__btn${guestMode === 'hotel' ? ' is-active' : ''}`}
                                    onClick={() => switchMode('hotel')}
                                >Hotel guest</button>
                            </div>
                        )}

                        <div className='resv-field resv-lookup'>
                            <label className='resv-label' htmlFor='resv-lookup'>
                                {guestMode === 'hotel' ? 'Find an in-house guest' : 'Find a saved guest'}
                            </label>
                            <div className='resv-lookup__row'>
                                <span className='resv-lookup__icon'>{icons.search}</span>
                                <input
                                    id='resv-lookup'
                                    className='resv-input resv-input--search'
                                    value={term}
                                    onChange={(e) => setTerm(e.target.value)}
                                    onFocus={() => setResultsOpen(true)}
                                    placeholder={guestMode === 'hotel' ? 'Room number, name or reservation' : 'Name, email or phone'}
                                />
                                {searching && <span className='resv-lookup__spin' />}
                            </div>

                            {resultsOpen && guestMode === 'hotel' && hotelResults.length > 0 && (
                                <div className='resv-results'>
                                    {hotelResults.map(g => (
                                        <button key={g.id} type='button' className='resv-result' onClick={() => pickHotelGuest(g)}>
                                            <span className='resv-result__name'>{guestFullName(g)}</span>
                                            <span className='resv-result__meta'>
                                                {g.roomNumber ? `Room ${g.roomNumber}` : 'No room'}
                                                {g.rateName ? ` · ${g.rateName}` : ''}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {resultsOpen && guestMode === 'walkIn' && customers.length > 0 && (
                                <div className='resv-results'>
                                    {customers.map(c => (
                                        <button key={c.id} type='button' className='resv-result' onClick={() => pickCustomer(c)}>
                                            <span className='resv-result__name'>{`${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email}</span>
                                            <span className='resv-result__meta'>{c.email || c.phone || '—'}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {(prostay || customerId) && (
                            <div className='resv-linked'>
                                <span className='resv-linked__text'>
                                    {prostay
                                        ? `Linked to hotel stay ${prostay.reservationNumber || prostay.reservationId}${prostay.roomNumber ? ` · Room ${prostay.roomNumber}` : ''}`
                                        : 'Linked to a saved guest profile'}
                                </span>
                                <button type='button' className='resv-linked__clear' onClick={clearGuestLink}>Unlink</button>
                            </div>
                        )}

                        <div className='resv-field'>
                            <label className='resv-label' htmlFor='resv-name'>Guest name<span className='resv-req'>*</span></label>
                            <input
                                id='resv-name'
                                className='resv-input'
                                value={guestName}
                                onChange={(e) => setGuestName(e.target.value)}
                                placeholder='Full name'
                            />
                        </div>
                        <div className='resv-grid2'>
                            <div className='resv-field'>
                                <label className='resv-label' htmlFor='resv-phone'>Phone</label>
                                <input id='resv-phone' type='tel' className='resv-input' value={phone} onChange={(e) => setPhone(e.target.value)} />
                            </div>
                            <div className='resv-field'>
                                <label className='resv-label' htmlFor='resv-email'>Email</label>
                                <input id='resv-email' type='email' className='resv-input' value={email} onChange={(e) => setEmail(e.target.value)} />
                            </div>
                        </div>
                    </section>

                    {/* ── Where ────────────────────────────────────────────── */}
                    <section className='resv-sec'>
                        <div className='resv-sec__title'>Where</div>
                        <div className='resv-tablepick'>
                            <div className='resv-tablepick__text'>
                                <span className='resv-tablepick__label'>Table</span>
                                <span className={`resv-tablepick__val${tableLabel ? '' : ' is-empty'}`}>
                                    {tableLabel || 'Not assigned yet'}
                                </span>
                            </div>
                            {isEdit ? (
                                <button
                                    type='button'
                                    className='resv-btn resv-btn--ghost'
                                    onClick={() => { onPickTables(reservation!); onClose(); }}
                                >Pick on floor</button>
                            ) : tableIds.length === 0 && (
                                // Only worth saying while there is nothing there: a booking
                                // started from a gap on the timeline arrives with its table.
                                <span className='resv-tablepick__hint'>Assign it on the floor after saving</span>
                            )}
                        </div>
                    </section>

                    {/* ── Admin-defined fields ─────────────────────────────── */}
                    {formFields.length > 0 && (
                        <section className='resv-sec'>
                            <div className='resv-sec__title'>Details</div>
                            {formFields.map(f => (
                                <ReservationFieldInput
                                    key={f.id}
                                    field={f}
                                    value={customFields[f.key]}
                                    profileBacked={f.onCustomerProfile && !!customerId}
                                    onChange={(key, value) => setCustomFields(prev => ({ ...prev, [key]: value }))}
                                />
                            ))}
                        </section>
                    )}

                    {/* ── Notes ────────────────────────────────────────────── */}
                    <section className='resv-sec'>
                        <div className='resv-sec__title'>Notes</div>
                        <div className='resv-field'>
                            <label className='resv-label' htmlFor='resv-gnotes'>Guest request</label>
                            <textarea
                                id='resv-gnotes'
                                className='resv-input resv-input--area'
                                rows={2}
                                value={guestNotes}
                                onChange={(e) => setGuestNotes(e.target.value)}
                                placeholder='Anything the guest asked for'
                            />
                        </div>
                        <div className='resv-field'>
                            <label className='resv-label' htmlFor='resv-inotes'>Internal note</label>
                            <textarea
                                id='resv-inotes'
                                className='resv-input resv-input--area'
                                rows={2}
                                value={internalNotes}
                                onChange={(e) => setInternalNotes(e.target.value)}
                                placeholder='Only the team sees this'
                            />
                        </div>
                    </section>

                    {isEdit && !isClosed && (
                        <section className='resv-sec'>
                            <div className='resv-sec__title'>Move it along</div>
                            <div className='resv-statusrow'>
                                {reservation!.status === 'requested' && (
                                    <button type='button' className='resv-btn resv-btn--ghost' onClick={() => handleStatus('confirmed')}>Confirm</button>
                                )}
                                {reservation!.status === 'confirmed' && (
                                    <button type='button' className='resv-btn resv-btn--ghost' onClick={() => handleStatus('arrived')}>Mark arrived</button>
                                )}
                                {(reservation!.status === 'confirmed' || reservation!.status === 'arrived') && onSeat && (
                                    <button type='button' className='resv-btn resv-btn--ghost' onClick={() => { onSeat(reservation!); onClose(); }}>Seat party</button>
                                )}
                                <button type='button' className='resv-btn resv-btn--warn' onClick={() => handleStatus('noShow')}>No show</button>
                                <button type='button' className='resv-btn resv-btn--danger' onClick={() => handleStatus('cancelled')}>Cancel booking</button>
                            </div>
                        </section>
                    )}
                </div>

                <div className='resv-sheet__foot'>
                    {error && <span className='resv-error'>{error}</span>}

                    {/* Refused, not failed: the booking is still here to change, and
                      * overlapping on purpose is a decision the host is allowed to make. */}
                    {clashes.length > 0 && (
                        <div className='resv-clash' role='alert'>
                            <div className='resv-clash__head'>
                                {icons.warn}
                                {clashes.length === 1
                                    ? `${clashes[0].tableNo || 'That table'} is already taken`
                                    : `${clashes.length} tables are already taken`}
                            </div>
                            <ul className='resv-clash__list'>
                                {clashes.map(c => (
                                    <li key={`${c.reservationId}-${c.tableId}`}>
                                        <b>{c.tableNo || c.tableId}</b> — {c.time} {c.guestName || 'booking'} ({c.partySize})
                                    </li>
                                ))}
                            </ul>
                            <div className='resv-clash__note'>
                                Change the time or the table, or seat both if you know the earlier
                                party will have gone.
                            </div>
                        </div>
                    )}

                    <div className='resv-sheet__footbtns'>
                        <button type='button' className='resv-btn resv-btn--ghost' onClick={onClose}>Close</button>
                        {clashes.length > 0 && (
                            <button
                                type='button'
                                className='resv-btn resv-btn--warn'
                                onClick={() => handleSave(true)}
                                disabled={saving}
                            >Book anyway</button>
                        )}
                        <button
                            type='button'
                            className='resv-btn resv-btn--primary'
                            onClick={() => handleSave()}
                            disabled={saving}
                        >
                            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create reservation'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReservationSheet;
